import { Button, IconButton, Stack, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import CommonDialog from '../notificationOverlay/CommonDialog'
import InCardStack from 'components/atoms/container/InCardStack'
import { InfoCardIcon } from 'components/atoms/Icons'
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight'
import LinearProgressBar from 'components/atoms/LinearProgressBar'
import calendarLinkIcon from 'assets/images/calendarLink.svg'
import videoMeetingMonitorWebcamIcon from 'assets/images/videoMeetingMonitorWebcam.svg'
import laptopComputerSmileyIcon from 'assets/images/laptopComputerSmiley.svg'
import unlinkBrokenChainIcon from 'assets/images/unlinkBrokenChain.svg'
import calendarQuestionIcon from 'assets/images/calendarQuestion.svg'
import calendarDateIcon from 'assets/images/calendarDate.svg'
import contentBrowserEditIcon from 'assets/images/contentBrowserEdit.svg'
import calendarAddIcon from 'assets/images/calendarAdd.svg'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import RowStack from 'components/atoms/container/RowStack'
import MicrosoftCalendar from '../oAuth/MicrosoftCalendar'
import GoogleCalendarLogin from '../oAuth/GoogleCalendar'
import NotificationSnackBar from '../notificationOverlay/NotificationSnackBar'
import { useNavigate } from 'react-router-dom'
import { MY_MEETING } from 'routes/constants'
import { getFullName } from 'utils/formatText'
import { parseToDateTimeObject } from 'utils/formatDateTime'
import { triggerOpenBookOneOnOne } from 'components/organisms/myTeam/BookOneOnOne'

export const SkipForNowButton = ({ onExit }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const handleClose = confirmed => {
    if (confirmed) {
      onExit()
    }
    setOpen(false)
  }
  return (
    <>
      <Button color='muted' onClick={() => setOpen(true)}>
        {t('addRaters.skipForNow')}
      </Button>
      <CommonDialog
        open={open}
        onClose={handleClose}
        titleText={t('calendarEvents.skipImporting')}
        confirmText={t('calendarEvents.yesSkip')}
        cancelText={t('common.cancel')}>
        <Typography>{t('calendarEvents.skipImportingDetail1')}</Typography>
        <Typography>{t('calendarEvents.skipImportingDetail2')}</Typography>
      </CommonDialog>
    </>
  )
}

export const DialogBodyContainer = ({
  title,
  icon,
  description,
  currentProgress,
  stepCount,
  children,
  progressBar = true
}) => {
  const { t } = useTranslation()
  return (
    <Grid size={12}>
      <InCardStack spacing={8} width='100%'>
        <InfoCardIcon src={icon} size={72} />
        <Typography
          variant='h1'
          textAlign='center'
          color='primary'
          maxWidth={500}>
          {title}
        </Typography>
        {progressBar && (
          <LinearProgressBar
            value={(currentProgress / stepCount) * 100}
            label={`${t('common.step')} ${currentProgress}/${stepCount}`}
          />
        )}
        <Typography
          textAlign='center'
          variant='body1Large'
          maxWidth={500}
          fontWeight={500}>
          {description}
        </Typography>
        {children}
      </InCardStack>
    </Grid>
  )
}

export const LinkCalendarStep = ({ handleSigninSuccess, onExit }) => {
  const { t } = useTranslation()
  const [error, setError] = useState(null)
  const handleSigninResult = success => {
    if (success) {
      handleSigninSuccess()
    } else {
      setError(true)
    }
  }
  return (
    <DialogBodyContainer
      icon={calendarLinkIcon}
      title={t('calendarEvents.linkCalendarFirst')}
      description={t('calendarEvents.linkCalendarFirstDetail')}
      progressBar={false}>
      <Stack padding={0} spacing={4}>
        <MicrosoftCalendar signinCallback={handleSigninResult} />
        <GoogleCalendarLogin signinCallback={handleSigninResult} />
      </Stack>
      <RowStack justifyContent='flex-end'>
        <Button onClick={onExit} color='muted'>
          {t('common.notNow')}
        </Button>
      </RowStack>
      <NotificationSnackBar
        open={error}
        setOpen={setError}
        errorText={t('calendarEvents.failedToLink')}
      />
    </DialogBodyContainer>
  )
}

const InfoItemContainer = ({ children }) => {
  return (
    <BoxWithBackground width={500} sx={{ padding: 8 }}>
      {children}
    </BoxWithBackground>
  )
}

export const CalendarLinkedStep = ({ stepCount, currentProgress }) => {
  const { t } = useTranslation()
  const InfoItem = ({ icon, description }) => (
    <InfoItemContainer>
      <RowStack>
        <InfoCardIcon size={56} src={icon} />
        <Typography variant='body1Large'>{description}</Typography>
      </RowStack>
    </InfoItemContainer>
  )
  return (
    <>
      <DialogBodyContainer
        icon={calendarLinkIcon}
        title={t('calendarEvents.calendarLinked')}
        description={t('calendarEvents.importExisting1on1')}
        currentProgress={currentProgress}
        stepCount={stepCount}
      />
      <InCardStack width='100%' spacing={8} paddingTop={8}>
        <InfoItem
          icon={videoMeetingMonitorWebcamIcon}
          description={t('calendarEvents.importDetail1')}
        />
        <InfoItem
          icon={laptopComputerSmileyIcon}
          description={t('calendarEvents.importDetail2')}
        />
        <InfoItem
          icon={unlinkBrokenChainIcon}
          description={t('calendarEvents.importDetail3')}
        />
      </InCardStack>
      <Grid size={12}>
        <InCardStack spacing={8} width='100%'></InCardStack>
      </Grid>
    </>
  )
}

export const NoMeetingFoundStep = ({ onClose }) => {
  const { t } = useTranslation()
  return (
    <DialogBodyContainer
      title={t('calendarEvents.no1on1')}
      icon={calendarQuestionIcon}
      description={t('calendarEvents.no1on1Detail')}
      progressBar={false}>
      <Typography textAlign='center'>
        <Trans i18nKey={'calendarEvents.tryImportAgain'} />
      </Typography>
      <RowStack justifyContent='flex-end'>
        <Button onClick={onClose}>{t('rolePlay.okay')}</Button>
      </RowStack>
    </DialogBodyContainer>
  )
}

export const FinishImportStep = ({ importedEvent, stepCount, onExit }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const InfoItem = ({ icon, children, action }) => (
    <InfoItemContainer>
      <RowStack justifyContent='space-between'>
        <RowStack>
          <InfoCardIcon size={56} src={icon} />
          {children}
        </RowStack>
        <IconButton onClick={action}>
          <ArrowCircleRightIcon fontSize='large' />
        </IconButton>
      </RowStack>
    </InfoItemContainer>
  )
  const attendeeName = importedEvent?.attendeeUser
    ? getFullName(importedEvent?.attendeeUser)
    : importedEvent?.attendee?.fullName || importedEvent?.attendee?.email

  const handleBookMeeting = () => {
    onExit()
    triggerOpenBookOneOnOne()
  }
  return (
    <DialogBodyContainer
      icon={calendarDateIcon}
      title={t('calendarEvents.allSet')}
      description={t('calendarEvents.addedDescription')}
      currentProgress={stepCount}
      stepCount={stepCount}>
      <Typography textAlign='center'>
        {t('calendarEvents.directReportInvited')}
      </Typography>
      {importedEvent && (
        <InfoItem
          icon={contentBrowserEditIcon}
          action={() =>
            navigate(MY_MEETING, {
              state: { userConnectionEventId: importedEvent.id }
            })
          }>
          <Stack padding={0}>
            <Typography>{t('calendarEvents.prepareForNext1on1')}</Typography>
            <Typography variant='h4'>{attendeeName}</Typography>
            <Typography>
              {parseToDateTimeObject(
                importedEvent.startTime,
                i18n.resolvedLanguage
              ).format('dddd, MMMM D')}
            </Typography>
          </Stack>
        </InfoItem>
      )}
      <InfoItem icon={calendarAddIcon} action={handleBookMeeting}>
        <Typography variant='h4'>
          {t('calendarEvents.createANew1on1')}
        </Typography>
      </InfoItem>
    </DialogBodyContainer>
  )
}
